class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pageBody = document.getElementsByTagName('body')[0];
    this._mobileTabsInitialised = false;

    this.setupEventListeners();
    window.SearchPopup?.mountPanels(this.querySelector('[data-search-panels]'));
    window.SearchPopup?.bindMobileTabClicks?.(this);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showIdle();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    this.saveLastSearched();
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a, [aria-selected="true"] [data-search-phrase]')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showIdle();
    }
  }

  onFocus() {
    this.closest('search-modal')?.open?.();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showIdle();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('data-search-state') === 'empty') {
      // Keep empty-state popup; do not reopen the results panel.
      return;
    } else if (this.getAttribute('results') === 'true' || this.hasAttribute('results')) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    const relatedTarget = event?.relatedTarget || null;
    setTimeout(() => {
      const active = document.activeElement;
      // iOS keyboard "Done" blurs the input with no next focus target — keep popup open.
      if (!relatedTarget && (!active || active === document.body || active === document.documentElement)) {
        return;
      }

      const modal = this.closest('search-modal');
      if (modal) {
        if (modal.contains(active)) return;
        modal.close?.(false);
        return;
      }
      if (!this.contains(active)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.showIdle();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') && this.getAttribute('data-search-state') !== 'results') return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item, button.button')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] [data-search-phrase], button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=12&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku`,
      {
        signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          this.showEmpty();
          return;
        }

        return response.text();
      })
      .then((text) => {
        if (!text || signal.aborted || searchTerm !== this.getQuery()) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(resultsMarkup, 'text/html');
          const products = Array.from(
            doc.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item')
          );
          const collections = Array.from(
            doc.querySelectorAll('#predictive-search-results-categories-list .predictive-search__list-item')
          );
          const productHandles = products
            .map((p) => p.querySelector('[data-product-handle]')?.getAttribute('data-product-handle'))
            .filter(Boolean);

          publish(PUB_SUB_EVENTS.searchQuery, {
            query: searchTerm,
            search_context: 'overlay',
            engine: 'shopify',
            products_count: products.length,
            collections_count: collections.length,
            result_product_ids: productHandles,
          });
        }
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError' || signal.aborted) {
          return;
        }
        this.showEmpty();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    if (typeof window.SearchPopup?.showLoadingSkeleton === 'function') {
      window.SearchPopup.showLoadingSkeleton(this);
    } else {
      this.setAttribute('loading', true);
      window.SearchPopup?.setSearchState(this, 'results');
    }
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this._mobileTabsInitialised = false;

    const isEmpty = Boolean(this.predictiveSearchResults.querySelector('[data-predictive-search-empty-trigger]'));
    if (isEmpty) {
      this.showEmpty();
      return;
    }

    this._ensureSidebarFallback();
    window.SearchPopup?.dedupeMobileProductTabs?.(this.predictiveSearchResults);
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this.highlightSearchedWord();
    window.SearchPopup?.bindPhraseChips?.(this.predictiveSearchResults);
    this.mobileTabs();
  }

  /**
   * When Shopify returns no phrases/collections, show Most searched +
   * Favorite categories from Search popup settings.
   */
  _ensureSidebarFallback() {
    const groups = this.predictiveSearchResults.querySelector(
      '#predictive-search-results-groups-wrapper, .search-popup__layout'
    );
    if (!groups) return;
    if (groups.querySelector('.search-popup__sidebar .search-popup__section')) return;

    const fallbackTpl = document.getElementById('search-popup-sidebar-fallback');
    if (!fallbackTpl?.content) return;

    const frag = fallbackTpl.content.cloneNode(true);
    const aside = frag.querySelector('[data-search-sidebar-fallback]');
    if (!aside?.querySelector('.search-popup__section--phrases, .search-popup__section--categories')) {
      return;
    }

    const hasProducts = Boolean(groups.querySelector('#predictive-search-results-products'));
    const tabProducts = frag.querySelector('[data-tab-products]');
    const tabCategories = frag.querySelector('[data-tab-categories]');

    if (tabProducts) {
      if (hasProducts) {
        const productCount = groups.querySelectorAll(
          '#predictive-search-results-products-list .predictive-search__list-item'
        ).length;
        tabProducts.hidden = false;
        tabProducts.innerHTML = `${tabProducts.textContent.trim()} <span>(${productCount})</span>`;
      } else {
        tabProducts.remove();
      }
    }

    if (tabCategories) {
      if (aside.querySelector('.search-popup__section--categories')) {
        tabCategories.hidden = false;
      } else {
        tabCategories.remove();
      }
    }

    groups.insertBefore(frag, groups.firstChild);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  showIdle() {
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', true);
    this.input.setAttribute('aria-activedescendant', '');
    this.isOpen = true;
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showIdle(this);
    // Keep idle panel visible while modal is open
    const idle = this.querySelector('[data-search-panel="idle"]');
    if (idle) idle.hidden = false;
  }

  showEmpty() {
    this.removeAttribute('loading');
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showEmpty(this);
  }

  open() {
    this.predictiveSearchResults?.style.removeProperty('max-height');
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showResults(this);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if (this.pageBody.classList.contains('is_fixed')) {
      this.pageBody.classList.remove('is_fixed');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.searchTerm = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
    this.querySelectorAll('[data-search-panel]').forEach((panel) => {
      panel.hidden = true;
    });
  }

  highlightSearchedWord() {
    const currval = this.input.value;
    const onlyLower = currval === currval.toLowerCase();
    const filterItems = this.querySelectorAll('[data-highlight]');
    for (let i = 0; i < filterItems.length; i++) {
      const textcontent = filterItems[i].textContent;
      const finalval = textcontent.replace(new RegExp(`(${currval})`, 'i'), function (_, p1) {
        if (onlyLower) {
          return '<strong>' + p1 + '</strong>';
        } else {
          return '<strong>' + currval + '</strong>';
        }
      });
      filterItems[i].innerHTML = finalval;
    }
  }

  mobileClick(target) {
    window.SearchPopup?.activateMobileTab?.(target);
  }

  mobileTabs() {
    window.SearchPopup?.bindMobileTabClicks?.(this);
    window.SearchPopup?.syncDefaultMobileTab?.(this);
    this._mobileTabsInitialised = true;
  }

  saveLastSearched() {
    let formSearchValues = [];
    const storedSearchValues = localStorage.getItem('lastSearched');
    formSearchValues = storedSearchValues ? JSON.parse(storedSearchValues) : [];
    const inputValue = this.input.value;
    formSearchValues.push(inputValue);
    if (formSearchValues.length > 5) {
      formSearchValues = formSearchValues.slice(formSearchValues.length - 5);
    }
    localStorage.setItem('lastSearched', JSON.stringify(formSearchValues));
  }
}

customElements.define('predictive-search', PredictiveSearch);
